import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UserContext } from '../App';
import { supabase } from '../utils/supabaseClient';
import { MapPin, Briefcase, Calendar, Loader2, MoreVertical, Flag, Ban, AlertTriangle, User, Sparkles } from 'lucide-react';
import { getZodiacSign, calculateAge, isBirthdayToday } from '../utils/zodiac';
import ReportModal from '../components/common/ReportModal';
import { AvatarDisplay } from '../utils/avatars';
import { useModal } from '../context/ModalContext';
import { BackButton } from '../components/common/PageHeader';
import { getLevelInfo } from '../utils/levels';
import { getMbtiStyle } from '../utils/mbti';
import { useIsDesktop } from '../hooks/useIsDesktop';

import {
  STAY_LABELS, TRAVEL_STYLE_LABELS, ACTIVITY_TIME_LABELS, FOOD_LABELS,
  ADVENTURE_LABELS, LANGUAGE_LABELS, SKILL_LABELS, RED_FLAG_LABELS, INTEREST_LABELS
} from '../utils/travelProfileConstants';

export default function TravelUserDetail() {
  const isDesktop = useIsDesktop();
  const { userId } = useParams();
  const navigate = useNavigate();
  const { currentUser, accessToken, setBlockedIds } = useContext(UserContext);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const menuRef = useRef(null);
  const { showAlert, showConfirm } = useModal();

  const isOwnProfile = currentUser && user && (currentUser.id === user.id || currentUser._id === user.id);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        setIsLoading(true);

        const { data: userProfile, error: userError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (userError || !userProfile) throw new Error('Failed to fetch user data');

        const { data: travelProfile } = await supabase
          .from('travel_profiles')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle();

        setUser({
          _id: userProfile.id,
          id: userProfile.id,
          auth0Id: userProfile.id,
          profile: userProfile,
          travelProfile: travelProfile || null
        });

      } catch (error) {
        console.error('Error loading user data:', error);
        showAlert('錯誤', '載入用戶資料失敗');
        navigate(-1);
      } finally {
        setIsLoading(false);
      }
    };

    if (userId) {
      loadUserData();
    }
  }, [userId, navigate, showAlert]);

  const handleBlockUser = async () => {
    setShowMenu(false);

    try {
      if (!currentUser || (!currentUser.id && !currentUser._id)) throw new Error('User not authenticated');
      const currentUserId = currentUser.id || currentUser._id;

      if (currentUserId === userId) {
        showAlert('提示', '您不能封鎖自己。');
        return;
      }

      showConfirm('確認封鎖', '確定要封鎖此對象？封鎖後雙方將無法看到彼此。', async () => {
        try {
          const { error } = await supabase
            .from('blocked_users')
            .insert({
              blocker_id: currentUserId,
              blocked_id: userId
            });

          if (error) {
            if (error.code === '23505') { // Unique violation
              showAlert('提示', '您已經封鎖過此對象。');
            } else {
              console.error('Supabase error:', error);
              showAlert('錯誤', `封鎖失敗: ${error.message || '未知錯誤'}`);
            }
            return;
          } else {
            if (setBlockedIds) {
              setBlockedIds(prev => Array.from(new Set([...(prev || []), userId])));
            }
            showAlert('成功', '已成功封鎖此對象。');
            navigate(-1);
          }
        } catch (error) {
          console.error('封鎖失敗:', error);
          showAlert('錯誤', `封鎖發生錯誤: ${error.message || '請稍後再試'}`);
        }
      }, true);
    } catch (error) {
      console.error('封鎖失敗:', error);
      showAlert('錯誤', `封鎖發生錯誤: ${error.message || '請稍後再試'}`);
    }
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh'
      }}>
        <Loader2 style={{ width: '24px', height: '24px', animation: 'spin 1s linear infinite' }} />
        <span style={{ fontSize: '1.2rem', marginLeft: '12px' }}>載入中...</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh', flexDirection: 'column'
      }}>
        <h2>找不到用戶</h2>
        <button
          onClick={() => navigate(-1)}
          style={{ marginTop: '16px', padding: '12px 24px', backgroundColor: '#4e8bca', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
        >返回</button>
      </div>
    );
  }

  const profile = user.profile || {};
  const tp = user.travelProfile;

  const renderChip = (label) => (
    <span style={{
      padding: '8px 16px', borderRadius: '20px', backgroundColor: '#f3f4f6', color: '#374151',
      fontSize: '0.875rem', fontWeight: '500', display: 'inline-block'
    }}>
      {label}
    </span>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #eef4f9 0%, #f5f9fc 18%, #ffffff 48%)',
      paddingBottom: '80px',
      overflowX: 'clip'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        maxWidth: isDesktop ? '760px' : '100%',
        margin: '0 auto'
      }}>
        <BackButton onClick={() => navigate(-1)} label="返回上一頁" />

        {/* More menu */}
        {!isOwnProfile && (
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setShowMenu(v => !v)}
              style={{
                background: 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(8px)',
                border: 'none',
                borderRadius: '50%',
                width: '36px', height: '36px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              <MoreVertical size={18} color="#374151" />
            </button>

            {showMenu && (
              <div style={{ position: 'absolute', top: '44px', right: 0, background: 'white', borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)', border: '1px solid #f1f5f9', minWidth: '150px', overflow: 'hidden', zIndex: 100 }}>
                <button
                  onClick={() => { setShowMenu(false); setShowReportModal(true); }}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '13px 16px', background: 'transparent', border: 'none', borderBottom: '1px solid #f1f5f9', color: '#ef4444', fontWeight: '600', fontSize: '0.9rem', cursor: 'pointer', textAlign: 'left' }}
                >
                  <Flag size={16} /> 檢舉此用戶
                </button>
                <button
                  onClick={handleBlockUser}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '13px 16px', background: 'transparent', border: 'none', color: '#333', fontWeight: '600', fontSize: '0.9rem', cursor: 'pointer', textAlign: 'left' }}
                >
                  <Ban size={16} /> 封鎖此對象
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{
        maxWidth: isDesktop ? '760px' : '100%',
        margin: '0 auto',
        padding: '0 16px',
        position: 'relative',
        zIndex: 5
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '24px',
          padding: isDesktop ? '40px' : '32px 24px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
          marginBottom: '24px',
          position: 'relative'
        }}>

          {/* Top Info Layout */}
          <div style={{
             display: 'flex',
             flexDirection: isDesktop ? 'row' : 'column',
             alignItems: isDesktop ? 'flex-start' : 'center',
             gap: '24px',
             marginBottom: '24px'
          }}>
             {/* Avatar */}
             <div>
                <div style={{
                  padding: '4px',
                  background: 'white',
                  borderRadius: '50%',
                  display: 'inline-flex',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.06)'
                }}>
                  <AvatarDisplay avatarId={profile.avatar} size={isDesktop ? 100 : 90} />
                </div>
             </div>

             {/* Info Column */}
             <div style={{ flex: 1, textAlign: isDesktop ? 'left' : 'center', width: '100%', marginTop: isDesktop ? '8px' : 0 }}>
               <div style={{ display: 'flex', alignItems: 'center', justifyContent: isDesktop ? 'flex-start' : 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  <h1 style={{
                    fontSize: isDesktop ? '2rem' : '1.75rem',
                    fontWeight: '700',
                    color: '#1a1a1a',
                    margin: 0
                  }}>
                    {profile.name || '未知用戶'}
                  </h1>
                  {(() => {
                    const mbtiStyle = getMbtiStyle(profile.mbti);
                    return mbtiStyle && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '4px 12px',
                        borderRadius: '10px',
                        background: mbtiStyle.bg,
                        color: mbtiStyle.color,
                        fontSize: '13px',
                        fontWeight: '700',
                        letterSpacing: '0.05em'
                      }}>
                        {profile.mbti}
                      </span>
                    );
                  })()}
                  {profile.badge === 'founding' && (
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 12px',
                      borderRadius: '14px',
                      background: 'linear-gradient(135deg, #eef4f9, #fde68a)',
                      border: '1px solid #f59e0b',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#92400e'
                    }}>
                      🏅 創始會員
                    </span>
                  )}
                  {(() => {
                    const levelInfo = getLevelInfo(profile.total_points || 0);
                    return (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 12px',
                        borderRadius: '999px',
                        background: '#eef4f9',
                        border: '1px solid #4e8bca',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#4e8bca'
                      }}>
                        LV.{levelInfo.level}・{levelInfo.title}
                      </span>
                    );
                  })()}
               </div>

               <div style={{
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: isDesktop ? 'flex-start' : 'center',
                 gap: '8px',
                 color: '#6b7280',
                 fontSize: '0.95rem',
                 flexWrap: 'wrap',
                 lineHeight: '1.6'
               }}>
                 {profile.gender && (
                   <>
                     <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                       <User size={16} />
                       <span>{profile.gender === 'male' ? '男性' : profile.gender === 'female' ? '女性' : profile.gender}</span>
                     </div>
                     <span style={{ color: '#d1d5db' }}>•</span>
                   </>
                 )}

                 {(profile.birthday || profile.age) && (
                   <>
                     <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                       <Calendar size={16} />
                       <span>
                         {(() => {
                           const age = profile.birthday ? calculateAge(profile.birthday) : profile.age;
                           return `${age} 歲`;
                         })()}
                       </span>
                     </div>

                     {profile.birthday && getZodiacSign(profile.birthday) && (
                       <>
                         <span style={{ color: '#d1d5db' }}>•</span>
                         <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                           <Sparkles size={16} color="#8b5cf6" />
                           <span>{getZodiacSign(profile.birthday).name}</span>
                         </div>
                       </>
                     )}
                     {profile.birthday && isBirthdayToday(profile.birthday) && <span>🎂</span>}
                   </>
                 )}

                 {profile.city && (
                   <>
                     {(profile.birthday || profile.age || profile.gender) && <span style={{ color: '#d1d5db' }}>•</span>}
                     <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                       <MapPin size={16} />
                       <span>{profile.city}</span>
                     </div>
                   </>
                 )}
                 
                 {profile.occupation && (
                   <>
                     <span style={{ color: '#d1d5db' }}>•</span>
                     <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                       <Briefcase size={16} />
                       <span>{profile.occupation}</span>
                     </div>
                   </>
                 )}
               </div>
             </div>
          </div>

          {!tp ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b7280' }}>
              <AlertTriangle size={48} style={{ margin: '0 auto 16px', color: '#9ca3af' }} />
              <h3 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#374151', margin: '0 0 8px' }}>尚未建立旅伴檔案</h3>
              <p style={{ margin: 0, fontSize: '0.95rem' }}>此用戶目前還沒有填寫旅伴專屬資訊喔！</p>
            </div>
          ) : (
            <>
              {/* Introduction */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1a1a1a', margin: '0 0 12px 0' }}>自我介紹</h3>
                <p style={{ color: '#6b7280', fontSize: '0.95rem', lineHeight: '1.7', margin: 0, whiteSpace: 'pre-wrap' }}>
                  {tp.introduction || '此人未填寫自我介紹'}
                </p>
              </div>

              {/* Personal Traits / Travel Style */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1a1a1a', margin: '0 0 12px 0' }}>旅行偏好</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {tp.mbti && renderChip(tp.mbti)}
                  {tp.travel_style && renderChip(TRAVEL_STYLE_LABELS[tp.travel_style] || tp.travel_style)}
                  {tp.cigarette !== null && tp.cigarette !== undefined && renderChip(tp.cigarette ? '吸菸 Yes' : '不吸菸 No')}
                  {tp.adventure_level?.map(a => renderChip(ADVENTURE_LABELS[a] || a))}
                  {tp.stay_pref?.map(s => renderChip(STAY_LABELS[s] || s))}
                  {tp.food_option?.map(f => renderChip(FOOD_LABELS[f] || f))}
                  {tp.activity_time?.map(t => renderChip(ACTIVITY_TIME_LABELS[t] || t))}
                </div>
              </div>

              {/* Interests */}
              {tp.interests && tp.interests.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1a1a1a', margin: '0 0 12px 0' }}>興趣愛好</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {tp.interests.map((interest, idx) => <React.Fragment key={idx}>{renderChip(INTEREST_LABELS[interest] || interest)}</React.Fragment>)}
                  </div>
                </div>
              )}

              {/* Languages */}
              {tp.languages && tp.languages.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1a1a1a', margin: '0 0 12px 0' }}>語言能力</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {tp.languages.map((lang, idx) => <React.Fragment key={idx}>{renderChip(LANGUAGE_LABELS[lang] || lang)}</React.Fragment>)}
                  </div>
                </div>
              )}

              {/* Skills */}
              {tp.skills && tp.skills.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1a1a1a', margin: '0 0 12px 0' }}>旅行技能</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {tp.skills.map((skill, idx) => <React.Fragment key={idx}>{renderChip(SKILL_LABELS[skill] || skill)}</React.Fragment>)}
                  </div>
                </div>
              )}

              {/* Past Trips */}
              {tp.past_trips && tp.past_trips.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1a1a1a', margin: '0 0 12px 0' }}>旅行足跡</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {tp.past_trips.map((trip, idx) => <React.Fragment key={idx}>{renderChip(trip)}</React.Fragment>)}
                  </div>
                </div>
              )}

              {/* Red Flags */}
              {tp.red_flags && tp.red_flags.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#ef4444', margin: '0 0 12px 0' }}>不接受行為 (Red Flags)</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {tp.red_flags.map((flag, idx) => <React.Fragment key={idx}>{renderChip(RED_FLAG_LABELS[flag] || flag)}</React.Fragment>)}
                  </div>
                </div>
              )}
            </>
          )}

        </div>
      </div>

      {/* Report Modal */}
      {showReportModal && (
        <ReportModal
          targetUser={user}
          accessToken={accessToken}
          reporterId={currentUser?.id || currentUser?._id}
          onClose={() => setShowReportModal(false)}
        />
      )}
    </div>
  );
}
